/**
 * Conversation UI Storage
 *
 * Stores the UI representation of conversations (messages with components)
 * separately from the backend conversation storage (raw AI messages).
 *
 * This allows us to:
 * - Display rich UI when viewing conversation history
 * - Keep backend storage focused on AI continuation
 * - Avoid mixing UI concerns with AI message history
 */
const UI_STORAGE_KEY_PREFIX = 'conversation_ui_';
const UI_INDEX_KEY = 'conversation_ui_index';
const MAX_UI_MESSAGES_ON_QUOTA = 200;
const MAX_MESSAGE_CONTENT_CHARS = 12000;
const MAX_COMPONENT_CONTENT_CHARS = 12000;
const MAX_TOOL_FIELDS_TO_KEEP = 20;
const MAX_TOOL_RESULT_CHARS = 4000;
const MAX_IMAGE_ATTACHMENTS_PER_MESSAGE = 20;
/**
 * Save UI state for a conversation
 */
export async function saveConversationUI(conversationId, messages) {
    try {
        const uiState = {
            conversationId,
            messages,
            lastUpdated: Date.now(),
        };
        // Save to localStorage
        const key = `${UI_STORAGE_KEY_PREFIX}${conversationId}`;
        const serializedState = JSON.stringify(uiState);
        const saveResult = trySetItem(key, serializedState);
        if (!saveResult.success) {
            if (isQuotaExceededError(saveResult.error)) {
                const recovered = attemptQuotaRecovery(conversationId, serializedState);
                if (!recovered) {
                    const trimmedMessages = trimMessagesForQuota(messages);
                    if (trimmedMessages.length < messages.length) {
                        const trimmedState = {
                            ...uiState,
                            messages: trimmedMessages,
                        };
                        const trimmedSave = trySetItem(key, JSON.stringify(trimmedState));
                        if (trimmedSave.success) {
                            saveUIIndex(conversationId, trimmedMessages.length, trimmedState.lastUpdated);
                            console.warn(`[ConversationUIStorage] Storage quota exceeded; saved trimmed UI state for ${conversationId} (${trimmedMessages.length}/${messages.length} messages)`);
                            return;
                        }
                    }
                    // Final fallback: compact oversized payloads (tool args/results/components)
                    const compactMessages = compactMessagesForStorage(messages);
                    const compactState = {
                        ...uiState,
                        messages: compactMessages,
                    };
                    const compactSave = trySetItem(key, JSON.stringify(compactState));
                    if (compactSave.success) {
                        saveUIIndex(conversationId, compactMessages.length, compactState.lastUpdated);
                        console.warn(`[ConversationUIStorage] Storage quota exceeded; saved compact UI state for ${conversationId}`);
                        return;
                    }
                }
                else {
                    saveUIIndex(conversationId, messages.length, uiState.lastUpdated);
                    return;
                }
            }
            console.error('[ConversationUIStorage] Failed to save UI state:', saveResult.error);
            return;
        }
        // Update index
        saveUIIndex(conversationId, messages.length, uiState.lastUpdated);
        console.log(`[ConversationUIStorage] Saved UI state for ${conversationId} (${messages.length} messages)`);
    }
    catch (error) {
        console.error('[ConversationUIStorage] Failed to save UI state:', error);
    }
}
/**
 * Load UI state for a conversation
 */
export async function loadConversationUI(conversationId) {
    try {
        const key = `${UI_STORAGE_KEY_PREFIX}${conversationId}`;
        const data = localStorage.getItem(key);
        if (!data) {
            console.log(`[ConversationUIStorage] No UI state found for ${conversationId}`);
            return null;
        }
        const uiState = JSON.parse(data);
        console.log(`[ConversationUIStorage] Loaded UI state for ${conversationId} (${uiState.messages.length} messages)`);
        return uiState;
    }
    catch (error) {
        console.error('[ConversationUIStorage] Failed to load UI state:', error);
        return null;
    }
}
/**
 * Delete UI state for a conversation
 */
export async function deleteConversationUI(conversationId) {
    try {
        const key = `${UI_STORAGE_KEY_PREFIX}${conversationId}`;
        localStorage.removeItem(key);
        // Update index
        const index = getUIIndex();
        delete index[conversationId];
        localStorage.setItem(UI_INDEX_KEY, JSON.stringify(index));
        console.log(`[ConversationUIStorage] Deleted UI state for ${conversationId}`);
    }
    catch (error) {
        console.error('[ConversationUIStorage] Failed to delete UI state:', error);
    }
}
/**
 * Get the UI index (list of all stored UI states)
 */
function getUIIndex() {
    try {
        const data = localStorage.getItem(UI_INDEX_KEY);
        return data ? JSON.parse(data) : {};
    }
    catch (error) {
        console.error('[ConversationUIStorage] Failed to load UI index:', error);
        return {};
    }
}
function saveUIIndex(conversationId, messageCount, lastUpdated) {
    const index = getUIIndex();
    index[conversationId] = { lastUpdated, messageCount };
    const saveResult = trySetItem(UI_INDEX_KEY, JSON.stringify(index));
    if (!saveResult.success) {
        if (isQuotaExceededError(saveResult.error)) {
            // Minimal fallback: keep only current conversation entry.
            const minimalIndex = {
                [conversationId]: { lastUpdated, messageCount },
            };
            const minimalSave = trySetItem(UI_INDEX_KEY, JSON.stringify(minimalIndex));
            if (minimalSave.success) {
                console.warn('[ConversationUIStorage] Storage quota exceeded; stored minimal UI index');
                return;
            }
        }
        console.error('[ConversationUIStorage] Failed to save UI index:', saveResult.error);
    }
}
function trySetItem(key, value) {
    try {
        localStorage.setItem(key, value);
        return { success: true };
    }
    catch (error) {
        return { success: false, error };
    }
}
function isQuotaExceededError(error) {
    if (!error || typeof error !== 'object') {
        return false;
    }
    const maybeDomError = error;
    return maybeDomError.name === 'QuotaExceededError' || maybeDomError.code === 22;
}
function attemptQuotaRecovery(conversationId, serializedState) {
    const index = getUIIndex();
    const entries = Object.entries(index)
        .filter(([id]) => id !== conversationId)
        .sort((a, b) => a[1].lastUpdated - b[1].lastUpdated);
    if (entries.length === 0) {
        return false;
    }
    const removeCount = Math.ceil(entries.length / 2);
    for (const [id] of entries.slice(0, removeCount)) {
        localStorage.removeItem(`${UI_STORAGE_KEY_PREFIX}${id}`);
        delete index[id];
    }
    const retry = trySetItem(`${UI_STORAGE_KEY_PREFIX}${conversationId}`, serializedState);
    if (!retry.success) {
        return false;
    }
    const indexSave = trySetItem(UI_INDEX_KEY, JSON.stringify(index));
    if (!indexSave.success) {
        console.warn('[ConversationUIStorage] Storage quota exceeded; failed to update UI index after cleanup');
    }
    console.warn(`[ConversationUIStorage] Storage quota exceeded; removed ${removeCount} old UI entries to recover`);
    return true;
}
function trimMessagesForQuota(messages) {
    if (messages.length <= MAX_UI_MESSAGES_ON_QUOTA) {
        return messages;
    }
    return messages.slice(-MAX_UI_MESSAGES_ON_QUOTA);
}
function compactMessagesForStorage(messages) {
    return messages.map((message) => {
        const compactMessage = {
            ...message,
            content: truncateText(message.content, MAX_MESSAGE_CONTENT_CHARS),
        };
        if (message.toolCall) {
            // Keep only lightweight, user-visible tool metadata.
            compactMessage.toolCall = {
                id: message.toolCall.id,
                name: message.toolCall.name,
                success: message.toolCall.success,
                status: message.toolCall.status,
            };
        }
        if (message.components?.length) {
            compactMessage.components = message.components.map((component) => {
                if (component.type === 'tool_start' || component.type === 'tool_complete') {
                    return {
                        ...component,
                        content: compactToolComponentContent(component.content),
                    };
                }
                return {
                    ...component,
                    content: truncateText(component.content, MAX_COMPONENT_CONTENT_CHARS),
                };
            });
        }
        if (Array.isArray(message.userImageAttachments)) {
            compactMessage.userImageAttachments = message.userImageAttachments
                .slice(0, MAX_IMAGE_ATTACHMENTS_PER_MESSAGE)
                .map((attachment) => ({
                contextId: attachment.contextId,
                name: truncateText(attachment.name || '', 256),
                mimeType: attachment.mimeType,
                originalMimeType: attachment.originalMimeType,
                converted: attachment.converted,
                sizeBytes: attachment.sizeBytes,
            }));
        }
        return compactMessage;
    });
}
function compactToolComponentContent(content) {
    try {
        const parsed = JSON.parse(content);
        const fields = Array.isArray(parsed.fields)
            ? parsed.fields.slice(0, MAX_TOOL_FIELDS_TO_KEEP).map((field) => ({
                name: field?.name ?? 'field',
                value: summarizeValue(field?.value),
            }))
            : [];
        const compact = {
            id: parsed.id,
            name: parsed.name,
            status: parsed.status,
            error: parsed.error,
            fields,
            result: summarizeValue(parsed.result),
        };
        return JSON.stringify(compact);
    }
    catch {
        return truncateText(content, MAX_COMPONENT_CONTENT_CHARS);
    }
}
function summarizeValue(value) {
    if (value == null) {
        return '';
    }
    if (typeof value === 'string') {
        return truncateText(value, MAX_TOOL_RESULT_CHARS);
    }
    try {
        return truncateText(JSON.stringify(value), MAX_TOOL_RESULT_CHARS);
    }
    catch {
        return truncateText(String(value), MAX_TOOL_RESULT_CHARS);
    }
}
function truncateText(text, maxChars) {
    if (text.length <= maxChars) {
        return text;
    }
    return `${text.slice(0, maxChars)}… [truncated]`;
}
/**
 * Clear all UI storage (for cleanup/reset)
 */
export async function clearAllConversationUI() {
    try {
        const index = getUIIndex();
        // Remove all UI state entries
        for (const conversationId of Object.keys(index)) {
            const key = `${UI_STORAGE_KEY_PREFIX}${conversationId}`;
            localStorage.removeItem(key);
        }
        // Clear index
        localStorage.removeItem(UI_INDEX_KEY);
        console.log('[ConversationUIStorage] Cleared all UI storage');
    }
    catch (error) {
        console.error('[ConversationUIStorage] Failed to clear UI storage:', error);
    }
}
/**
 * Get UI index for all stored conversations.
 */
export function getConversationUIIndex() {
    return getUIIndex();
}
